import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const err = (e) => e?.response?.data?.detail || "Something went wrong";
const CONTRACT = { signed: "Signed by the owner", awaiting_seller: "Sent to the owner for signing", amendment_awaiting_seller: "Amendment sent to the owner for signing" };

const SENT = { sent: "Invitation emailed", sandbox_skipped: "Preview sandbox — invitation not emailed", failed: "Invitation email failed" };

// Trainer view: the contract is the owner's — show only its status.
export function TrainerContractStatus({ a }) {
  const signed = a.contract_status === "signed";
  const waiting = ["awaiting_seller", "amendment_awaiting_seller"].includes(a.contract_status);
  return (
    <section data-testid="trainer-contract-status" className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Advertising Agreement</div>
      <p data-testid="trainer-contract-status-label" className={`font-semibold ${signed ? "text-emerald-800" : "text-stone-800"}`}>
        Contract Status: {signed ? "Signed" : waiting ? "Waiting for Seller to Sign" : "Being prepared"}
      </p>
      <p className="text-xs text-stone-500 mt-1">
        {signed ? `Signed by the owner (${a.contact?.full_name || "seller"}). The Ad Template is ready for you to work on.`
          : "The advertising agreement has been sent to the horse's owner for signature. You can continue preparing the horse's details, photos and videos while we wait — the Ad Template opens once it's signed."}
      </p>
    </section>
  );
}

// Owner view: who else can manage this horse, invite a trainer, revoke access.
export function TrainerAccessCard({ appId }) {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => { api.get(`/my/applications/${appId}/trainers`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [appId]);
  useEffect(() => { load(); }, [load]);
  const invite = async () => {
    try {
      const r = await api.post(`/my/applications/${appId}/trainers`, f);
      setF({ name: "", email: "", phone: "" });
      setAdding(false);
      (r.data.email_status === "sent" ? toast.success : toast.warning)(`Trainer access added. ${SENT[r.data.email_status] || ""}${r.data.email_error && r.data.email_status === "failed" ? `: ${r.data.email_error}` : ""}`);
      load();
    } catch (e) { toast.error(err(e)); }
  };
  const revoke = async (t) => {
    if (!window.confirm(`Remove ${t.name || t.email}'s access to this horse?`)) return;
    try { await api.delete(`/my/applications/${appId}/trainers/${t.id}`); toast.success("Access removed"); load(); } catch (e) { toast.error(err(e)); }
  };
  const resend = async (t) => {
    try { const r = await api.post(`/my/applications/${appId}/trainers/${t.id}/resend`); (r.data.email_status === "sent" ? toast.success : toast.warning)(SENT[r.data.email_status] || "Done"); load(); } catch (e) { toast.error(err(e)); }
  };
  if (!rows) return null;
  const active = rows.filter((t) => t.status === "active");
  const showForm = adding || !active.length;
  return (
    <section data-testid="trainer-access" className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Trainer access</div>
      <p className="text-xs text-stone-500">Trainers can complete details, photos and the Ad Template. Only you can sign the contract. We'll email them a secure sign-in invitation — no account needed.</p>
      {active.length ? active.map((t) => (
        <div key={t.id} data-testid={`trainer-access-${t.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm border-t pt-2">
          <div className="min-w-0">
            <p className="truncate"><b>{t.name || t.email}</b> · {t.email}</p>
            <p data-testid={`trainer-access-state-${t.id}`} className={`text-xs font-semibold ${t.signed_in ? "text-emerald-700" : "text-amber-700"}`}>{t.signed_in ? "Active" : "Invited — hasn't signed in yet"}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            {!t.signed_in && <Button data-testid={`trainer-resend-${t.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => resend(t)}>Resend invitation</Button>}
            <Button data-testid={`trainer-revoke-${t.id}`} size="sm" variant="outline" className="rounded-full text-red-700" onClick={() => revoke(t)}><UserX className="h-4 w-4 mr-1" />Remove</Button>
          </div>
        </div>
      )) : <p data-testid="trainer-access-none" className="text-sm text-stone-500">No trainer has access.</p>}
      {!showForm && <Button data-testid="trainer-add-another" size="sm" variant="outline" className="rounded-full" onClick={() => setAdding(true)}><UserPlus className="h-4 w-4 mr-1" />Add another trainer</Button>}
      {showForm && <div className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <Input data-testid="trainer-invite-name" placeholder="Trainer name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="trainer-invite-email" placeholder="Trainer email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input data-testid="trainer-invite-phone" placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <Button data-testid="trainer-invite-btn" disabled={!f.email || !f.name} onClick={invite} className="rounded-full"><UserPlus className="h-4 w-4 mr-1" />Invite trainer</Button>
      </div>}
    </section>
  );
}
